export const DEVICE_LOCK_KEY = 'pace-device-lock';
export const DEVICE_LOCK_CLIENTID = 'pace-device-lock-id';
export const DEVICE_LOCK_CLIENTID_CREATED_AT = 'pace-device-lock-id-created-at';

/**
 * Fingerprint of the device for locking purposes.
 *
 * Not to be confused with a security fingerprint, this is just a collection
 * of some basic information to help identify the device that holds the lock.
 * The clientId is the relevant field, as it is used to determine lock ownership.
 * The other fields are informational and can be used to display information about the lock owner.
 */
export interface DeviceLockFingerprint {
    os?: string;
    vendor?: string;
    model?: string;
    browser?: string;
    standalone?: boolean;
    clientId: string;
    isPrivateBrowser: boolean;
}

export function prettifyDeviceLockFingerprint(fingerprint: DeviceLockFingerprint): string {
    const clientId = fingerprint.clientId?.toUpperCase();
    if (!clientId) {
        return 'no clientId';
    }
    return `${clientId.substring(0, 4)}-${clientId.substring(4, 8)}`;
}

export interface LockState {
    ownedByMe: boolean;
    ownerFingerprint: DeviceLockFingerprint | null;
    acquiredAt: string | null;
}

export const INITIAL_LOCK_STATE: LockState = {
    ownedByMe: false,
    ownerFingerprint: null,
    acquiredAt: null,
};

export interface LockValue {
    fingerprint: DeviceLockFingerprint | null;
    acquiredAt: string | null;
}

export const EMPTY_DEVICE_LOCK: LockValue = {
    fingerprint: null,
    acquiredAt: null,
};

export function lockValueToState(value: LockValue | null, myFingerprint: DeviceLockFingerprint): LockState {
    return {
        ownedByMe: value?.fingerprint?.clientId === myFingerprint.clientId,
        ownerFingerprint: value?.fingerprint ?? null,
        acquiredAt: value?.acquiredAt ?? null,
    };
}

/**
 * The device finger print consist of a random UUID (clientId) and a set of deterministic properties, such as browser,
 * os, vendor and model. This helper function compares two fingerprints.
 *
 * @param state
 * @param myFingerprint
 * @param fingerprintA
 * @param fingerprintB
 */
export function isDeterministicFingerprintEqual(
    fingerprintA: DeviceLockFingerprint,
    fingerprintB: DeviceLockFingerprint,
): boolean {
    return (
        fingerprintA.browser === fingerprintB.browser &&
        fingerprintA.os === fingerprintB.os &&
        fingerprintA.model === fingerprintB.model &&
        fingerprintA.vendor === fingerprintB.vendor
    );
}

export function loadLocalLock(): LockValue | null {
    try {
        return JSON.parse(localStorage.getItem(DEVICE_LOCK_KEY) ?? 'null');
    } catch {
        return null;
    }
}

export function saveLocalLock(lock: LockValue | null): void {
    if (lock) {
        localStorage.setItem(DEVICE_LOCK_KEY, JSON.stringify(lock));
    } else {
        localStorage.removeItem(DEVICE_LOCK_KEY);
    }
}
